// Build metadata helpers shared by the app runtime and the deploy-time build pipeline.

export interface ChangelogEntry {
  commitSha: string;
  subject: string;
}

export interface ChangelogSnapshot {
  buildNumber: number;
  fullVersion: string;
  gitSha: string;
  buildTime: string;
  environment: string;
  entries: ChangelogEntry[];
}

export interface AppBuildInfo {
  appVersion: string;
  buildNumber: number;
  fullVersion: string;
  gitSha: string;
  buildTime: string;
  environment: string;
  changelogEntries: string[];
  changelogHistory: ChangelogSnapshot[];
}

const UNKNOWN_BUILD_TIME = "unknown";
const BUILD_BADGE_ENVIRONMENTS = new Set(["dev", "staging"]);

export const formatBuildNumber = (buildNumber: number) => String(buildNumber).padStart(4, "0");

const trimNonEmpty = (value: unknown) => (typeof value === "string" ? value.trim() : "");

export const normalizeChangelogEntry = (entry: unknown): ChangelogEntry | null => {
  if (typeof entry === "string") {
    const subject = entry.trim();
    return subject ? { commitSha: "unknown", subject } : null;
  }

  if (!entry || typeof entry !== "object") {
    return null;
  }

  const candidate = entry as Partial<ChangelogEntry> & { sha?: unknown; message?: unknown };
  const subject = trimNonEmpty(candidate.subject ?? candidate.message);
  if (!subject) {
    return null;
  }

  const commitSha = trimNonEmpty(candidate.commitSha ?? candidate.sha) || "unknown";

  return { commitSha, subject };
};

export const normalizeChangelogHistory = (
  rawBuildInfo: Partial<AppBuildInfo> | undefined,
  environment: string,
  fallbackFullVersion?: string,
): ChangelogSnapshot[] => {
  const rawHistory = Array.isArray((rawBuildInfo as { changelogHistory?: unknown })?.changelogHistory)
    ? ((rawBuildInfo as { changelogHistory?: unknown[] }).changelogHistory ?? [])
    : [];

  const normalizedHistory = rawHistory
    .map((snapshot) => {
      if (!snapshot || typeof snapshot !== "object") {
        return null;
      }

      const candidate = snapshot as Partial<ChangelogSnapshot> & { entries?: unknown };
      const entries = Array.isArray(candidate.entries) ? candidate.entries.map(normalizeChangelogEntry).filter(Boolean) : [];

      if (entries.length === 0) {
        return null;
      }

      const buildNumber =
        typeof candidate.buildNumber === "number" && Number.isFinite(candidate.buildNumber) && candidate.buildNumber >= 0
          ? candidate.buildNumber
          : 0;
      const fullVersion = trimNonEmpty(candidate.fullVersion) || trimNonEmpty(fallbackFullVersion) || `1.0.0.${formatBuildNumber(buildNumber)}`;
      const gitSha = trimNonEmpty(candidate.gitSha) || "unknown";
      const buildTime = trimNonEmpty(candidate.buildTime) || UNKNOWN_BUILD_TIME;
      const snapshotEnvironment = trimNonEmpty(candidate.environment) || environment;

      return {
        buildNumber,
        fullVersion,
        gitSha,
        buildTime,
        environment: snapshotEnvironment,
        entries: entries as ChangelogEntry[],
      };
    })
    .filter((snapshot): snapshot is ChangelogSnapshot => snapshot !== null);

  if (normalizedHistory.length > 0) {
    return normalizedHistory;
  }

  const legacyEntries = Array.isArray(rawBuildInfo?.changelogEntries)
    ? rawBuildInfo.changelogEntries.map(normalizeChangelogEntry).filter(Boolean)
    : [];

  if (legacyEntries.length === 0) {
    return [];
  }

  const buildNumber =
    typeof rawBuildInfo?.buildNumber === "number" && Number.isFinite(rawBuildInfo.buildNumber) && rawBuildInfo.buildNumber >= 0
      ? rawBuildInfo.buildNumber
      : 0;

  return [
    {
      buildNumber,
      fullVersion: trimNonEmpty(rawBuildInfo?.fullVersion) || trimNonEmpty(fallbackFullVersion) || `1.0.0.${formatBuildNumber(buildNumber)}`,
      gitSha: trimNonEmpty(rawBuildInfo?.gitSha) || "unknown",
      buildTime: trimNonEmpty(rawBuildInfo?.buildTime) || UNKNOWN_BUILD_TIME,
      environment: trimNonEmpty(rawBuildInfo?.environment) || environment,
      entries: legacyEntries as ChangelogEntry[],
    },
  ];
};

export const flattenChangelogHistory = (history: ChangelogSnapshot[]) =>
  history.flatMap((snapshot) => snapshot.entries.map((entry) => entry.subject));

export const buildChangelogHistory = (
  previousBuildInfo: Partial<AppBuildInfo> | undefined,
  currentBuildInfo: Pick<AppBuildInfo, "buildNumber" | "fullVersion" | "gitSha" | "buildTime" | "environment">,
  currentEntries: ChangelogEntry[],
) => {
  const previousHistory = normalizeChangelogHistory(previousBuildInfo, currentBuildInfo.environment, previousBuildInfo?.fullVersion);
  return currentEntries.length > 0
    ? [
        {
          ...currentBuildInfo,
          entries: currentEntries,
        },
        ...previousHistory,
      ]
    : previousHistory;
};

export const buildBackfilledChangelogHistory = (
  previousBuildInfo: Partial<AppBuildInfo> | undefined,
  currentBuildInfo: Pick<AppBuildInfo, "buildNumber" | "fullVersion" | "gitSha" | "buildTime" | "environment">,
  currentEntries: ChangelogEntry[],
  recentEntries: ChangelogEntry[],
) => {
  const currentEntryShas = new Set(currentEntries.map((entry) => entry.commitSha));
  const seededEntries = recentEntries.filter((entry) => !currentEntryShas.has(entry.commitSha));
  const previousBuildNumber =
    typeof previousBuildInfo?.buildNumber === "number" && Number.isFinite(previousBuildInfo.buildNumber) && previousBuildInfo.buildNumber >= 0
      ? previousBuildInfo.buildNumber
      : Math.max(0, currentBuildInfo.buildNumber - 1);
  const previousFullVersion =
    trimNonEmpty(previousBuildInfo?.fullVersion) || `1.0.0.${formatBuildNumber(previousBuildNumber)}`;
  const previousGitSha = trimNonEmpty(previousBuildInfo?.gitSha) || "unknown";
  const previousBuildTime = trimNonEmpty(previousBuildInfo?.buildTime) || currentBuildInfo.buildTime;
  const previousEnvironment = trimNonEmpty(previousBuildInfo?.environment) || currentBuildInfo.environment;
  const seededHistory = seededEntries.length > 0
    ? [
        {
          buildNumber: previousBuildNumber,
          fullVersion: previousFullVersion,
          gitSha: previousGitSha,
          buildTime: previousBuildTime,
          environment: previousEnvironment,
          entries: seededEntries,
        },
      ]
    : [];

  return currentEntries.length > 0
    ? [
        {
          ...currentBuildInfo,
          entries: currentEntries,
        },
        ...seededHistory,
      ]
    : seededHistory;
};

export const createFallbackBuildInfo = (environment: string): AppBuildInfo => ({
  appVersion: "1.0.0",
  buildNumber: 0,
  fullVersion: `1.0.0.${formatBuildNumber(0)}`,
  gitSha: "unknown",
  buildTime: UNKNOWN_BUILD_TIME,
  environment,
  changelogEntries: [],
  changelogHistory: [],
});

export const normalizeBuildInfo = (
  rawBuildInfo: Partial<AppBuildInfo> | undefined,
  environment: string,
): AppBuildInfo => {
  const fallback = createFallbackBuildInfo(environment);

  const buildNumber =
    typeof rawBuildInfo?.buildNumber === "number" && Number.isFinite(rawBuildInfo.buildNumber) && rawBuildInfo.buildNumber >= 0
      ? rawBuildInfo.buildNumber
      : fallback.buildNumber;

  const appVersion = rawBuildInfo?.appVersion?.trim() || fallback.appVersion;
  const gitSha = rawBuildInfo?.gitSha?.trim() || fallback.gitSha;
  const buildTime = rawBuildInfo?.buildTime?.trim() || fallback.buildTime;
  const resolvedEnvironment = rawBuildInfo?.environment?.trim() || environment;
  const resolvedFullVersion = rawBuildInfo?.fullVersion?.trim() || `${appVersion}.${formatBuildNumber(buildNumber)}`;
  const changelogHistory = normalizeChangelogHistory(rawBuildInfo, resolvedEnvironment, resolvedFullVersion);
  const changelogEntries = changelogHistory.length > 0 ? flattenChangelogHistory(changelogHistory) : fallback.changelogEntries;

  return {
    appVersion,
    buildNumber,
    fullVersion: resolvedFullVersion,
    gitSha,
    buildTime,
    environment: resolvedEnvironment,
    changelogEntries,
    changelogHistory,
  };
};

export const getRuntimeBuildEnvironment = () => (import.meta as ImportMeta & { env?: { VITE_APP_ENV?: string } }).env?.VITE_APP_ENV ?? "unknown";

export const readAppBuildInfo = (environment = getRuntimeBuildEnvironment()) =>
  normalizeBuildInfo((globalThis as typeof globalThis & { __APP_BUILD_INFO__?: Partial<AppBuildInfo> }).__APP_BUILD_INFO__, environment);

export const appBuildInfo = readAppBuildInfo();

export const shouldShowBuildBadge = (environment: string) => BUILD_BADGE_ENVIRONMENTS.has(environment);

export const formatBuildTimestamp = (buildTime: string) => {
  if (!buildTime || buildTime === UNKNOWN_BUILD_TIME) {
    return UNKNOWN_BUILD_TIME;
  }

  const parsedDate = new Date(buildTime);
  if (Number.isNaN(parsedDate.getTime())) {
    return buildTime;
  }

  try {
    return parsedDate.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZoneName: "short",
    });
  } catch {
    return parsedDate.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }
};
